import { createAxiosInstance } from '@app/common';
import { CmsApiResponse } from '@app/models';

interface PreviewFetchOptions<T> {
    axiosInstance: ReturnType<typeof createAxiosInstance>;
    endpoint: string;
    documentId: string;
    populate: Record<string, any> | string;
    entityName: string;
    postProcess?: (data: T) => T | Promise<T>;
}

/**
 * Fetches a single document in preview/draft mode from CMS
 *
 * @param options - Configuration options for fetching preview document
 * @returns The fetched document or null if not found
 */
export async function fetchPreviewDocument<T>(options: PreviewFetchOptions<T>): Promise<T | null> {
    const { axiosInstance, endpoint, documentId, populate, entityName, postProcess } = options;

    const response = await axiosInstance.get<CmsApiResponse<T>>(`/${endpoint}/${documentId}`, {
        params: {
            status: 'draft',
            populate,
        },
    });

    const data = response.data?.data;

    if (!data) {
        console.log(`[CMS] ${entityName} not found:`, { documentId });
        return null;
    }

    return postProcess ? await postProcess(data) : data;
}

interface DatesConfig {
    startDate?: string | null;
    endDate?: string | null;
}

/**
 * Checks if content is active within the date range
 *
 * @param config - Content configuration with date range
 * @param hasRequiredContent - Function to check if required content exists
 * @returns True if content is within date range
 */
export function isDateRangeValid<T extends { config?: DatesConfig }>(
    content: T,
    hasRequiredContent: (content: T) => boolean,
): boolean {
    if (!content.config || !hasRequiredContent(content)) {
        return false;
    }

    const now = new Date();
    const startDate = content.config.startDate ? new Date(content.config.startDate) : now;
    const endDate = content.config.endDate ? new Date(content.config.endDate) : now;

    return now >= startDate && now <= endDate;
}

/**
 * Sorts sections in place by their order field
 *
 * @param data - Object containing sections array
 * @param sectionsKey - Key name for the sections array (default: 'sections')
 */
export function sortSectionsByOrder<T extends Record<string, any>>(
    data: T,
    sectionsKey = 'sections',
): void {
    const sections = data[sectionsKey];
    if (Array.isArray(sections) && sections.length > 0) {
        sections.sort((a: any, b: any) => a.order - b.order);
    }
}
